import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setRefreshSidebar } from "../redux/sidebarSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/ChatPage.css";
import send from "../assets/icons/send.png";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import Swal from "sweetalert2";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import editIcon from "../assets/icons/editIcon.png";
import cpyIcon from "../assets/icons/copyIcon.png";
import crossIcon from "../assets/icons/crossIcon.png";
import errorImg from "../assets/icons/errorImg.png";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import api from "../api";
import fileIcon from "../assets/icons/fileIcon.png";
import { setIsChatPage } from "../redux/sessionSlice";
import { branding } from "../utils/branding";

const DocumentChatPage = ({ firstMessage }) => {
  const selectedModel = useSelector((state) => state.model.selectedModel);
  const selectedMode = useSelector((state) => state.model.selectedMode);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const hasInteractedRef = useRef(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isBotResponding, setIsBotResponding] = useState(false);
  const typingIntervalRef = useRef(null);
  const [shouldForceScroll, setShouldForceScroll] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const messages1 = useSelector((state) => state.chat.messages);
  const searchTerm = useSelector((state) => state.chat.searchTerm);

  const [micOpen, setMicOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [tempTranscript, setTempTranscript] = useState(""); // NEW
  const [interimTranscript, setInterimTranscript] = useState(""); // live speech preview
  const recognitionRef = useRef(null);
  // State to track if reading is in progress
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Start recording
  const startRecording = () => {
    if (
      !("webkitSpeechRecognition" in window || "SpeechRecognition" in window)
    ) {
      showErrorToast("Your browser does not support speech recognition.");
      return;
    }

    setIsRecording(true);
    setTempTranscript(""); // reset final transcript
    setInterimTranscript(""); // reset live preview

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;

    // Use a separate variable to accumulate final results
    let finalBuffer = tempTranscript;

    recognition.onresult = (event) => {
      let interim = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalBuffer += result[0].transcript + " "; // append final
        } else {
          interim += result[0].transcript; // live preview
        }
      }

      setTempTranscript(finalBuffer); // update accumulated final
      setInterimTranscript(interim); // update live preview
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  // ✅ Stop recording and insert final transcript into input
  const stopRecording = () => {
    setIsRecording(false);

    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    setInput((prev) => prev + tempTranscript.trim()); // insert **all** captured speech
    setMicOpen(false);
    setTempTranscript(""); // reset final
    setInterimTranscript(""); // reset preview
  };

  // ❌ Cancel recording and discard
  const cancelRecording = () => {
    setIsRecording(false);

    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    setMicOpen(false);
    setTempTranscript(""); // discard final
    setInterimTranscript(""); // discard preview
  };

  // Function to toggle reading aloud
  const toggleReadAloud = () => {
    if (!input) return;

    if (window.speechSynthesis.speaking) {
      // Stop current speech
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      const utterance = new SpeechSynthesisUtterance(input);
      utterance.lang = "en-US";

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      window.speechSynthesis.speak(utterance);
    }
  };

  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [filesUploaded, setFilesUploaded] = useState(false); // track upload completion

  // Trigger file input click
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Add files with initial uploading status
    const newFiles = files.map((file) => ({
      file,
      status: "uploading",
    }));
    setAttachedFiles((prev) => [...prev, ...newFiles]);

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("model_name", selectedModel);
    formData.append("uploadType", "document");

    // ✅ Always send upload_mode = personal
    formData.append("upload_mode", "personal");

    try {
      const token =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");

      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showSuccessToast("Files uploaded successfully");

      // Update all newly added files to done
      setAttachedFiles((prev) =>
        prev.map((f) =>
          files.find((x) => x.name === f.file.name)
            ? { ...f, status: "done" }
            : f
        )
      );
    } catch (error) {
      console.error("Upload failed:", error);
      showErrorToast("Failed to upload files");

      // Mark them as error
      setAttachedFiles((prev) =>
        prev.map((f) =>
          files.find((x) => x.name === f.file.name)
            ? { ...f, status: "error" }
            : f
        )
      );
    }

    e.target.value = ""; // reset file input
  };

  // Remove a specific file by index
  const removeAttachedFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const [user, setUser] = useState({
    user_profile_pic: "",
    name: "",
    email: "",
  });
  const [loading, setLoading] = useState(true);
  const refreshKey = useSelector((state) => state.refresh.key);

  const filteredMessages = messages.filter((msg) =>
    msg.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // When the page loads, mark chat page as active
    dispatch(setIsChatPage(true));

    // When component unmounts (user leaves), mark it false
    return () => {
      dispatch(setIsChatPage(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.sessionId) {
      setSessionId(location.state.sessionId);
    }
  }, [location.state?.sessionId]);

  useEffect(() => {
    if (!firstMessage || hasInteractedRef.current) return;

    // If sessionId exists, send firstMessage immediately
    if (sessionId) {
      handleSend(firstMessage);
      hasInteractedRef.current = true;
    }
  }, [firstMessage, sessionId]);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (!chatBox) return;

    const isUserAtBottom =
      chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

    setShowScrollDown(!isUserAtBottom);

    const handleScroll = () => {
      const atBottom =
        chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;
      setShowScrollDown(!atBottom);
    };

    chatBox.addEventListener("scroll", handleScroll);

    return () => chatBox.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (!chatBox) return;

    const isUserAtBottom =
      chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

    if (shouldForceScroll || isUserAtBottom) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShouldForceScroll(false);
    }
  }, [messages, shouldForceScroll]);

  const fetchedSession = useRef(false);

  useEffect(() => {
    if (!sessionId || fetchedSession.current) return;
    fetchedSession.current = true;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    const fetchSessionHistory = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/chat/session/${sessionId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setModalMessage("Your session has expired. Please login again.");
          setShowModal(true);
          return;
        }

        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();

        if (!data || !Array.isArray(data.history)) return;

        const formatted = data.history.map((msg) => ({
          id: msg.id,
          sender: msg.sender,
          message: msg.message,
          time: msg.time,
          userName: msg.userName,
          userPic: msg.userPic,
        }));

        setMessages(formatted);
        dispatch(setMessages(formatted)); // Redux store
      } catch (err) {
        console.error("Failed to fetch chat session:", err);
        setModalMessage("Failed to load chat history. Please try again.");
        setShowModal(true);
      }
    };

    fetchSessionHistory();
  }, [sessionId]);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");

    const handleScroll = () => {
      if (!chatBox) return;

      const isAtBottom =
        chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

      setShowScrollDown(!isAtBottom);
    };

    chatBox?.addEventListener("scroll", handleScroll);
    return () => chatBox?.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      const token =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/user-profile/profile`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch profile");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [refreshKey]); // 🔑 refreshKey triggers re-fetch

  const storedUser =
    JSON.parse(localStorage.getItem("user")) ||
    JSON.parse(sessionStorage.getItem("user"));

  const loggedInName = user?.user_name || "User";
  const loggedInPic = user?.user_profile_pic
    ? `${
        process.env.REACT_APP_SERVER_URL
      }/${storedUser.user_profile_pic.replace(/\\/g, "/")}`
    : "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";

  const handleSend = async (customInput) => {
    const finalInput = customInput ?? input;
    if (!finalInput.trim()) return;

    if (!selectedModel) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please select a model before sending a message.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Unauthorized",
        text: "Please login again.",
      });
      return;
    }

    // ---------------- Append attached file names only ONCE ----------------
    let messageToSend = finalInput;
    const uploadedFiles = attachedFiles
      .filter((f) => f.status === "done")
      .map((f) => f.file.name)
      .join(", ");

    if (uploadedFiles) {
      messageToSend += messageToSend
        ? ` (Attached: ${uploadedFiles})`
        : `(Attached: ${uploadedFiles})`;
    }

    // ✅ Reset attached files right after using them (only once)
    if (uploadedFiles) {
      setAttachedFiles([]);
    }

    const time = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const filesForMessage = attachedFiles
      .filter((f) => f.status === "done")
      .map((f) => ({
        name: f.file.name,
        size: f.file.size,
        type: f.file.type,
      }));

    const userMsg = {
      id: Date.now(),
      sender: "user",
      message: finalInput,
      files: filesForMessage, // ✅ include files array
      time,
      userName: loggedInName,
      userPic: loggedInPic,
    };

    setMessages((prev) => [...prev, userMsg]);
    setShouldForceScroll(true);
    setInput("");

    const textarea = document.querySelector(".input-textarea");
    if (textarea) textarea.style.height = "40px";

    const botTypingId = Date.now() + 1;
    const botTypingMsg = {
      id: botTypingId,
      sender: "bot",
      message: "",
      partialMessage: "",
      showDots: true,
      time,
    };

    setMessages((prev) => [...prev, botTypingMsg]);
    setIsBotResponding(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/send-message`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: finalInput, // 🧠 use original text for processing
            model: selectedModel || "llama-3.3-70b-versatile",
            session_id: sessionId,
            search_mode: selectedMode,
            enable_web_search: true,
            enable_vector_search: true,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to fetch");

      const botData = await response.json();
      const fullResponse =
        botData.response ||
        "Apologies, we couldn't process your request. Please try again.";

      if (botData.session_id && botData.session_id !== sessionId) {
        setSessionId(botData.session_id);
      }

      dispatch(setRefreshSidebar(true));

      setTimeout(() => {
        let charIndex = 0;
        typingIntervalRef.current = setInterval(() => {
          charIndex++;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    partialMessage: fullResponse.slice(0, charIndex),
                    showDots: false,
                  }
                : msg
            )
          );

          if (charIndex >= fullResponse.length) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === botTypingId
                  ? {
                      ...msg,
                      message: fullResponse,
                      partialMessage: "",
                      showDots: false,
                    }
                  : msg
              )
            );
            setIsBotResponding(false);
          }
        }, 15);
      }, 500);
    } catch (error) {
      console.error("💥 Chat error:", error.message);
      clearInterval(typingIntervalRef.current);
      setIsBotResponding(false);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botTypingId
            ? {
                ...msg,
                message:
                  "We apologize, but something went wrong. Please try again later.",
                partialMessage: "",
                showDots: false,
              }
            : msg
        )
      );
    }
  };

  const handleStopResponse = () => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    setIsBotResponding(false);
    setMessages((prev) =>
      prev.map((msg) =>
        msg.showDots
          ? { ...msg, showDots: false } // Only stop the dots, preserve partialMessage
          : msg
      )
    );
    window.speechSynthesis.cancel();
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast("Text copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleRetry = (botMsgId) => {
    const botIndex = messages.findIndex((msg) => msg.id === botMsgId);
    for (let i = botIndex - 1; i >= 0; i--) {
      if (messages[i].sender === "user") {
        handleSend(messages[i].message);
        break;
      }
    }
  };

  const handleEdit = (msg) => {
    if (msg.sender === "user") {
      setEditingId(msg.id);
      setInput(msg.message);
    }
  };

  const handleSaveEdit = () => {
    if (editingId !== null) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === editingId ? { ...msg, message: input } : msg
        )
      );
      setEditingId(null);
      setInput("");
    }
  };

  const renderMessage = (msg) => {
    const isBot = msg.sender === "bot";
    const profile = isBot
      ? "/img/botIcon.png"
      : loggedInPic ||
        "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";

    return (
      <div
        className={`d-flex ${
          isBot ? "flex-row" : "flex-row-reverse"
        } align-items-start my-3`}
        key={msg.id}
      >
        <div className="d-flex flex-column chat-message-container">
          <div
            className={`chat-bubble ${isBot ? "bot-bubble" : "user-bubble"}`}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <img
                src={profile}
                className={isBot ? "bot-avatar" : "chat-avatar"}
                alt="avatar"
                title="avtar"
              />
              <strong>
                {isBot ? branding("ZeroBox") : loggedInName || "User"}
              </strong>

              <small className="">{msg.time}</small>
            </div>

            <div className="formatted-text">
              {msg.files && msg.files.length > 0 && (
                <div className="attached-files">
                  {msg.files.map((file, idx) => (
                    <div
                      className="file-bubble d-flex align-items-center gap-2 p-2 rounded bg-light"
                      key={idx}
                    >
                      <img src={fileIcon} alt="File" width="22" />
                      {/* You can replace with SVG or icon */}

                      <div className="d-flex flex-column">
                        <small className="file-name">{file.name}</small>
                        <small className="file-size">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isBot && msg.showDots ? (
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || "");
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={oneDark}
                          language={match[1]}
                          PreTag="div"
                          customStyle={{
                            borderRadius: "10px",
                            padding: "1rem",
                          }}
                          {...props}
                        >
                          {String(children).replace(/\n$/, "")}
                        </SyntaxHighlighter>
                      ) : (
                        <code
                          className="bg-light px-2 py-1 rounded"
                          style={{ fontFamily: "monospace" }}
                          {...props}
                        >
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {msg.partialMessage || msg.message}
                </ReactMarkdown>
              )}
            </div>
          </div>

          {!msg.showDots && (
            <div
              className={`d-flex bot-icon-below gap-3 mt-1 ${
                isBot
                  ? "justify-content-start ms-3"
                  : "justify-content-end me-3"
              }`}
            >
              {isBot ? (
                <>
                  <i
                    className={`bi refresh-prompts ${
                      window.speechSynthesis.speaking &&
                      currentUtterance?.text === msg.message
                        ? "bi-volume-mute"
                        : "bi-volume-up"
                    }`}
                    role="button"
                    title={
                      window.speechSynthesis.speaking &&
                      currentUtterance?.text === msg.message
                        ? "Stop Sound"
                        : "Play Sound"
                    }
                    onClick={() => handleTextToSpeech(msg.message)}
                  />

                  <i
                    className="bi bi-clipboard refresh-prompts"
                    role="button"
                    title="Copy"
                    onClick={() => handleCopy(msg.message)}
                  />
                  <i
                    className="bi bi-arrow-clockwise refresh-prompts"
                    role="button"
                    title="Retry"
                    onClick={() => handleRetry(msg.id)}
                  />
                </>
              ) : (
                <>
                  {/* <img
                    src={cpyIcon}
                    alt="Copy"
                    className="icon-image"
                    role="button"
                    title="Copy"
                    onClick={() => handleCopy(msg.message)}
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  /> */}
                  <i
                    className="bi bi-clipboard"
                    role="button"
                    title="Copy"
                    onClick={() => handleCopy(msg.message)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  let currentUtterance = null;

  const handleTextToSpeech = (text) => {
    // If already speaking -> stop it first
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      // If same message clicked again → stop sound
      if (currentUtterance && currentUtterance.text === text) {
        currentUtterance = null;
        return;
      }
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);
    currentUtterance = utterance;

    // Optional: Set voice, pitch, rate, etc.
    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    // When finished → reset current utterance
    utterance.onend = () => {
      currentUtterance = null;
    };

    // Speak
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="w-100  h-100 overflow-hidden px-3 chat-wrapper">
      <div className="flex-grow-1 mt-5 overflow-auto chat-box">
        {filteredMessages.map((msg) => renderMessage(msg))}
        <div ref={chatEndRef} />
        {/* {showScrollDown && (
          <button
            className="scroll-down-btn"
            onClick={() =>
              chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
            }
          >
            ⬇
          </button>
        )} */}
        {showScrollDown && (
          <button
            className="scroll-down-btn"
            onClick={() =>
              chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
            }
          >
            <i className="bi bi-arrow-down" style={{ fontSize: "24px" }}></i>
          </button>
        )}
      </div>

      <div className="">
        <div
          className="rounded p-1 mt-1 input-area"
          style={{ border: "1px solid var(--dark-color)" }}
        >
          {/* ---------------- Hidden file input ---------------- */}
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* ---------------- Attached Files Preview ---------------- */}
          {attachedFiles.length > 0 && (
            <div className="d-flex flex-wrap mb-2">
              {attachedFiles.map((item, idx) => (
                <div
                  key={idx}
                  className="attached-file-chip position-relative me-2 rounded bg-light d-flex flex-column px-2 py-1"
                  style={{
                    maxWidth: "200px",
                    overflow: "visible",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    <img src={fileIcon} alt="PDF" width="18" />
                    <span
                      style={{
                        fontSize: "small",
                        display: "inline-block",
                        maxWidth: "180px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        verticalAlign: "middle",
                      }}
                      title={item.file.name}
                    >
                      {item.file.name}
                    </span>
                  </div>

                  <small style={{ fontSize: "10px", color: "#555" }}>
                    {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                  </small>

                  {item.status === "done" && (
                    <small style={{ fontSize: "10px", color: "green" }}>
                      Uploaded ✅
                    </small>
                  )}
                  {item.status === "error" && (
                    <small style={{ fontSize: "10px", color: "red" }}>
                      Failed ❌
                    </small>
                  )}

                  <img
                    src={crossIcon}
                    alt="Remove"
                    onClick={() => removeAttachedFile(idx)}
                    style={{
                      position: "absolute",
                      top: "-2px",
                      right: "-6px",
                      width: "14px",
                      height: "14px",
                      zIndex: 1003,
                      cursor: "pointer",
                    }}
                  />

                  {item.status === "uploading" && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(255, 255, 255, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "inherit",
                        zIndex: 2,
                      }}
                    >
                      <div
                        className="spinner-border"
                        role="status"
                        style={{
                          width: "20px",
                          height: "20px",
                          color: "var(--dark-color)",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ---------------- Textarea ---------------- */}
          <div className="mb-2">
            <textarea
              className="p-2 pb-4 border-0 shadow-none input-textarea"
              placeholder={
                isFocused
                  ? ""
                  : "How can {AppConfig.projectName} help you today?"
              }
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // Auto expand textarea height
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
                // ✅ फक्त text खूप झाल्यावर scroll दाखव
                if (e.target.scrollHeight > 200) {
                  e.target.style.overflowY = "auto";
                } else {
                  e.target.style.overflowY = "hidden";
                }
              }}
              rows={1}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!isBotResponding) {
                    handleSend();
                    // reset textarea height
                    e.target.style.height = "40px";
                    e.target.style.overflowY = "hidden"; // reset scroll लपवा
                    e.target.style.height = "auto";
                  }
                }
              }}
            />
          </div>

          {/* ---------------- Buttons ---------------- */}
          <div className="d-flex align-items-center justify-content-between rounded-3 p-1">
            <div className="d-flex gap-1">
              <button className="btn btn-link p-0" onClick={handleAttachClick}>
                <i className="bi bi-paperclip fs-5"></i>
              </button>

              <button
                className="btn btn-link p-0"
                onClick={toggleReadAloud}
                title={isSpeaking ? "Stop reading" : "Read aloud"}
              >
                <i
                  className={`bi ${
                    isSpeaking ? "bi-volume-mute" : "bi-volume-up"
                  } fs-5`}
                ></i>
              </button>

              <button
                className="btn btn-link p-0"
                onClick={() => {
                  setMicOpen(true);
                  startRecording();
                }}
              >
                <i className="bi bi-mic fs-5"></i>
              </button>
              {/* Mic Overlay */}
              {micOpen && (
                <div className="mic-overlay position-absolute start-50 translate-middle-x p-3">
                  {/* Waveform animation */}
                  <div className="mic-waveform d-flex gap-2 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="wave-dot"></span>
                    ))}
                  </div>

                  {/* Live preview of speech */}
                  <div
                    className="live-preview mb-2 p-2 rounded"
                    style={{
                      background: "#f8f9fa",
                      height: "37px",
                      fontSize: "14px",
                      overflowY: "auto",
                      overflowX: "hidden",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {interimTranscript || "Listening..."}
                  </div>

                  {/* Buttons */}
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm  d-flex align-items-center justify-content-center"
                      onClick={stopRecording}
                      title="Confirm"
                      style={{
                        color: "var(--btn-color)",
                        backgroundColor: "var(--primary-color)",
                      }}
                    >
                      <i
                        className="bi bi-check-lg"
                        style={{ fontSize: "14px" }}
                      ></i>
                    </button>
                    <button
                      className="btn btn-sm  d-flex align-items-center border justify-content-center"
                      onClick={cancelRecording}
                      title="Cancel"
                      style={{
                        color: "var(--primary-color)",
                        backgroundColor: "var(--btn-color)",
                      }}
                    >
                      <i
                        className="bi bi-x-lg"
                        style={{ fontSize: "14px" }}
                      ></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="custom-send-btn d-flex align-items-center justify-content-center"
              onClick={
                isBotResponding ? handleStopResponse : () => handleSend()
              }
              disabled={attachedFiles.some((f) => f.status === "uploading")}
              style={{
                opacity: attachedFiles.some((f) => f.status === "uploading")
                  ? 0.5
                  : 1,
                cursor: attachedFiles.some((f) => f.status === "uploading")
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              {isBotResponding ? (
                <i
                  className="bi bi-stop-fill rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: "30px",
                    height: "27px",
                    background: "#3e3e3e",
                    border: "none",
                  }}
                ></i>
              ) : (
                <img src={send} alt="Send" title="Send" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentChatPage;
